import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Monitor, Smartphone } from "lucide-react";
import { SUPPORT_EMAIL } from "@/lib/support";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/ThemeProvider";

export function DesktopOnlyScreen() {
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/zen-logo.png" : "/zen-black-logo.png";
  const [hoveredDevice, setHoveredDevice] = useState<string | null>(null);

  const devices = [
    {
      icon: Monitor,
      name: "Desktop & Laptop",
      description: "The good stuff ✨",
      status: "supported",
      size: "w-16 h-16",
    },
    {
      icon: Smartphone,
      name: "Mobile & Tablet",
      description: "We're working on it 🤞",
      status: "coming-soon",
      size: "w-12 h-12",
    },
  ];

  return (
    <div className="min-h-screen w-full flex flex-col bg-background">
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <img src={logoSrc} alt="Zen Logo" className="h-16" />
          </div>
          <h1 className="text-4xl font-bold -mb-4 mt-4 tracking-tight">
            Oops! Wrong Device
          </h1>
        </div>

        {/* Device Support Grid */}
        <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-12 max-w-2xl mx-auto">
          {devices.map((device, index) => {
            const Icon = device.icon;
            return (
              <div
                key={device.name}
                className={cn(
                  "relative rounded-2xl border border-border p-6 transition-all duration-300",
                  "bg-gradient-to-b from-card/80 to-card/40",
                  "hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20",
                  "flex flex-col items-center text-center",
                  hoveredDevice === device.name
                    ? "scale-105"
                    : hoveredDevice && hoveredDevice !== device.name
                    ? "scale-95 opacity-70"
                    : "",
                  device.status === "supported"
                    ? "border-green-500/20 bg-green-500/5"
                    : "border-orange-500/20 bg-orange-500/5"
                )}
                onMouseEnter={() => setHoveredDevice(device.name)}
                onMouseLeave={() => setHoveredDevice(null)}
              >
                <div className="mb-4">
                  <Icon
                    className={cn(
                      device.size,
                      device.status === "supported"
                        ? "text-green-500"
                        : "text-orange-500"
                    )}
                  />
                </div>
                <h3 className="font-semibold mb-2">{device.name}</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {device.description}
                </p>
                <div
                  className={cn(
                    "px-3 py-1 rounded-full text-xs font-medium",
                    device.status === "supported"
                      ? "bg-green-500/20 text-green-500"
                      : "bg-orange-500/20 text-orange-500"
                  )}
                >
                  {device.status === "supported" ? "✓ Supported" : "⏳ Soon"}
                </div>
              </div>
            );
          })}
        </div>

        {/* Feature Preview */}
        <div className="w-full max-w-4xl">
          <h2 className="text-2xl font-semibold text-center mb-8">
            Why Desktop? Because We're Extra Like That 🎵
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl border border-border p-6 bg-gradient-to-b from-card/80 to-card/40">
              <h3 className="font-semibold mb-2">
                All the Bells & Whistles 🎛️
              </h3>
              <p className="text-sm text-muted-foreground">
                We crammed so many features in here that your phone would
                probably explode. Desktop gives us the space to be our best
                selves.
              </p>
            </div>
            <div className="rounded-xl border border-border p-6 bg-gradient-to-b from-card/80 to-card/40">
              <h3 className="font-semibold mb-2">Professional Vibes Only 💼</h3>
              <p className="text-sm text-muted-foreground">
                This isn't your average music app – it's a full production
                suite. We need that big screen energy to make magic happen.
              </p>
            </div>
            <div className="rounded-xl border border-border p-6 bg-gradient-to-b from-card/80 to-card/40">
              <h3 className="font-semibold mb-2">Multitasking Mastery 🧠</h3>
              <p className="text-sm text-muted-foreground">
                Work on multiple projects, have 47 tabs open, and still look
                professional. Desktop life is the best life.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-6">
            Don't worry, we're not being mean – we just want to give you the
            best experience possible! Your desktop is calling… 📞
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="px-8"
              onClick={() => window.location.reload()}
            >
              Maybe I'll Try Again 🤷‍♂️
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="px-8"
              onClick={() =>
                window.open(`mailto:${SUPPORT_EMAIL}`, "_blank")
              }
            >
              Help! I'm Confused 😵
            </Button>
          </div>
        </div>
      </div>

      <footer className="p-6 text-center text-sm text-muted-foreground">
        © {new Date().getFullYear()} Zenmusic. We're picky about screens, but we mean well!
        💙
      </footer>
    </div>
  );
}
